import React, { useEffect, useRef, useState } from "react";
import { ArrowDownRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const HERO_VIDEO = "/videos/hero-1.mp4";
const HERO_POSTER = "/videos/hero-poster-v2.webp";

export const Hero = () => {
  const { t } = useLanguage();
  const [showVideo, setShowVideo] = useState(false);
  const videoRef = useRef(null);

  // Video mounts only after the page is fully loaded and the browser is idle.
  // Keeps the poster <img> as a stable LCP and prevents video frames from
  // hurting Speed Index during the measurement window.
  useEffect(() => {
    const trigger = () => setShowVideo(true);
    const schedule = () => {
      if ("requestIdleCallback" in window) {
        requestIdleCallback(trigger, { timeout: 3000 });
      } else {
        setTimeout(trigger, 1500);
      }
    };
    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
    }
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-[80svh] w-full overflow-hidden grain"
    >
      <div className="absolute inset-0">
        <img
          src={HERO_POSTER}
          alt=""
          fetchpriority="high"
          decoding="async"
          data-testid="hero-poster"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
            showVideo ? "opacity-0" : "opacity-100"
          }`}
        />
        {showVideo && (
          <video
            ref={videoRef}
            data-testid="hero-video"
            src={HERO_VIDEO}
            poster={HERO_POSTER}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1322]/55 via-[#1A1322]/35 to-[#1A1322]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1322]/75 via-[#1A1322]/25 to-transparent" />
        <div className="absolute inset-0 bg-[#512C89]/15 mix-blend-overlay" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-16 pt-28 pb-20 min-h-[80svh] flex flex-col justify-end lg:justify-center">
        <div className="max-w-3xl">
          <div className="eyebrow animate-fade-up text-[#FBAD18]" data-testid="hero-eyebrow">
            <span className="inline-block w-8 h-px bg-[#FBAD18] align-middle mr-3" />
            {t.hero.eyebrow}
          </div>

          <h1
            data-testid="hero-title"
            className="font-display mt-6 text-[13vw] sm:text-6xl lg:text-[7rem] leading-[0.95] tracking-[-0.04em] text-[#F5EFE6]"
          >
            <span className="block animate-fade-up delay-100 font-light">
              {t.hero.title_1}
            </span>
            <span className="block animate-fade-up delay-200 font-semibold text-[#FBAD18]">
              {t.hero.title_2}
            </span>
            <span className="block animate-fade-up delay-300 font-light">
              {t.hero.title_3}
            </span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#F5EFE6]/80 animate-fade-up delay-400 font-medium"
          >
            {t.hero.subtitle}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-4 animate-fade-up delay-400">
            <button
              data-testid="hero-cta-shop"
              onClick={() => scrollTo("productos")}
              className="group inline-flex items-center justify-center gap-3 bg-[#FBAD18] text-[#1A1322] hover:bg-[#D8910D] transition-colors px-8 py-4 text-xs tracking-[0.25em] uppercase font-bold rounded-full"
            >
              {t.hero.cta_shop}
              <ArrowDownRight
                size={16}
                className="transition-transform group-hover:translate-x-1 group-hover:translate-y-1"
              />
            </button>
            <button
              data-testid="hero-cta-story"
              onClick={() => scrollTo("ritual")}
              className="inline-flex items-center justify-center gap-3 bg-transparent text-[#F3BB72] hover:bg-[#FBAD18]/10 hover:text-[#FBAD18] border border-[#F3BB72]/40 px-8 py-4 text-xs tracking-[0.25em] uppercase font-semibold rounded-full transition-colors"
            >
              {t.hero.cta_story}
            </button>
          </div>
        </div>

        <div className="absolute bottom-8 right-6 sm:right-12 lg:right-16 text-[10px] tracking-[0.4em] uppercase font-semibold text-[#F3BB72]/70">
          {t.hero.eyebrow.split("·")[1]?.trim()}
        </div>
      </div>
    </section>
  );
};
